/**
 * useMediaPipePose — React hook that runs MediaPipe Pose estimation on a video element.
 *
 * Lazily loads the WASM model on first use.
 * Runs at ~30fps via requestAnimationFrame.
 * Yields pose landmarks per frame to a callback.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { PoseLandmarkerResult, NormalizedLandmark } from '@/lib/mediapipe-pose';

interface UseMediaPipePoseOptions {
  /** Video element ref to process */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Whether to actively run pose estimation */
  enabled: boolean;
  /** Called on each frame with the detected landmarks */
  onFrame: (landmarks: NormalizedLandmark[]) => void;
}

export function useMediaPipePose({ videoRef, enabled, onFrame }: UseMediaPipePoseOptions) {
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const landmarkerRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  // Load MediaPipe lazily
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Dynamic import so WASM is only loaded when needed
        const { getPoseLandmarker } = await import('@/lib/mediapipe-pose');
        const landmarker = await getPoseLandmarker();
        if (cancelled) return;
        landmarkerRef.current = landmarker;
        setReady(true);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[useMediaPipePose] Failed to load', err);
        setError('Failed to load pose model');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  // Run estimation loop
  useEffect(() => {
    if (!enabled || !ready || !landmarkerRef.current) return;

    const video = videoRef.current;
    if (!video) return;

    let running = true;

    const detect = () => {
      if (!running || !landmarkerRef.current) return;

      if (video.readyState >= 2 && video.videoWidth > 0) {
        const now = performance.now();
        // Throttle to ~30fps (33ms between frames)
        if (now - lastTimeRef.current >= 33) {
          try {
            const result: PoseLandmarkerResult = landmarkerRef.current.detectForVideo(video, now);
            if (result.landmarks?.[0]) {
              onFrameRef.current(result.landmarks[0]);
            }
          } catch {
            // Silently skip frame on detection error
          }
          lastTimeRef.current = now;
        }
      }

      rafRef.current = requestAnimationFrame(detect);
    };

    rafRef.current = requestAnimationFrame(detect);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, ready, videoRef]);

  return { loading, ready, error };
}
